import type { ContractorProfile, CRMUser, InstallLaborLine, InstallNodeData, ProposalNodeSet } from '../types';
import type { PayrollPosition, PayrollSettings } from '../types/finances';
import { WORK_DAYS_PER_YEAR } from '../types/finances';
import { contractorInstallLaborDayRate } from './contractorInvoiceBilling';
import { positionMonthlyContribution, type PositionCostContext } from './financesForecast';
import { is1099ContractorUser, isOpsHubEligibleRole } from './userRoles';

export const INSTALL_LABOR_OTHERS_ROLE = 'Others';

export function isOthersInstallLaborLine(
  line: Pick<InstallLaborLine, 'employeeUserId' | 'role'>,
): boolean {
  return !line.employeeUserId && (line.role || '').trim().toLowerCase() === 'others';
}

export function applyOthersLaborLine(line: InstallLaborLine): InstallLaborLine {
  const quantity = Number(line.quantity);
  return {
    ...line,
    role: INSTALL_LABOR_OTHERS_ROLE,
    employeeUserId: undefined,
    quantity: quantity > 0 ? quantity : 1,
  };
}

export function isMappedInstallLaborPerson(
  line: Pick<InstallLaborLine, 'employeeUserId' | 'role'>,
  people?: InstallLaborPerson[],
): boolean {
  if (isOthersInstallLaborLine(line)) return false;
  if (line.employeeUserId) return true;
  if (!people?.length) return false;
  return !!matchInstallLaborPerson(people, { name: line.role })?.userId;
}

export function laborLineEffectiveQuantity(
  line: Pick<InstallLaborLine, 'quantity' | 'employeeUserId' | 'role'>,
  people?: InstallLaborPerson[],
): number {
  return isMappedInstallLaborPerson(line, people) ? 1 : (Number(line.quantity) || 0);
}

export function laborLineDayTotal(
  line: Pick<InstallLaborLine, 'quantity' | 'ratePerDay' | 'employeeUserId' | 'role'>,
  people?: InstallLaborPerson[],
): number {
  return Math.round(laborLineEffectiveQuantity(line, people) * (Number(line.ratePerDay) || 0) * 100) / 100;
}

/** On-site days × this line’s daily cost. */
export function laborLineJobTotal(
  line: Pick<InstallLaborLine, 'quantity' | 'ratePerDay' | 'employeeUserId' | 'role'>,
  days: number,
  people?: InstallLaborPerson[],
): number {
  return Math.round(laborLineDayTotal(line, people) * (Number(days) || 0) * 100) / 100;
}

export function laborLinesDayTotal(lines: InstallLaborLine[] | undefined): number {
  return (lines || []).reduce((sum, line) => sum + laborLineDayTotal(line), 0);
}

export function isBlankLaborLine(line: InstallLaborLine): boolean {
  return !line.role?.trim() && !(Number(line.quantity) > 0 && Number(line.ratePerDay) > 0);
}

export function emptyInstallLaborLine(): InstallLaborLine {
  return {
    id:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? `labor-${crypto.randomUUID()}`
        : `labor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role: '',
    quantity: 1,
    ratePerDay: 0,
  };
}

/** Explicit lines when present; otherwise one Crew row from the existing day rate. */
export function getInstallLaborLines(install: Pick<InstallNodeData, 'laborLines' | 'teamLaborPerDay'>): InstallLaborLine[] {
  if (Array.isArray(install.laborLines) && install.laborLines.length > 0) {
    return install.laborLines;
  }
  if ((install.teamLaborPerDay || 0) > 0) {
    return [
      {
        id: 'legacy-crew',
        role: 'Crew',
        quantity: 1,
        ratePerDay: install.teamLaborPerDay,
      },
    ];
  }
  return [];
}

export type InstallLaborPerson = {
  id: string;
  name: string;
  title?: string;
  userId?: string;
  ratePerDay: number | null;
};

export function installLaborPersonLabel(person: InstallLaborPerson): string {
  const title = (person.title || '').trim();
  if (title && title.toLowerCase() !== person.name.trim().toLowerCase()) {
    return `${person.name} · ${title}`;
  }
  return person.name;
}

export function installLaborDayRateFromMonthly(monthlyLoaded: number): number {
  if (!(monthlyLoaded > 0)) return 0;
  return Math.round(((monthlyLoaded * 12) / WORK_DAYS_PER_YEAR) * 100) / 100;
}

export function installLaborDayRateForPosition(
  position: PayrollPosition,
  settings: PayrollSettings,
  context: PositionCostContext = {},
  asOf = new Date(),
): number {
  return installLaborDayRateFromMonthly(positionMonthlyContribution(position, asOf, settings, context));
}

export function matchInstallLaborPerson(
  people: InstallLaborPerson[],
  query: { userId?: string; name?: string },
): InstallLaborPerson | undefined {
  if (query.userId) {
    const byUser = people.find((person) => person.userId && person.userId === query.userId);
    if (byUser) return byUser;
  }
  const name = (query.name || '').trim().toLowerCase();
  if (!name) return undefined;
  return people.find((person) => person.name.trim().toLowerCase() === name);
}

function rateForPosition(
  position: PayrollPosition | undefined,
  settings: PayrollSettings,
  location?: string | null,
): number | null {
  if (!position) return null;
  const rate = installLaborDayRateForPosition(position, settings, { crmUserLocation: location });
  return rate > 0 ? rate : null;
}

/** Last $/day saved on install labor lines in proposal order (later structures win). */
export function lastInstallDayRateByUserIdFromNodeSets(
  nodeSets: Pick<ProposalNodeSet, 'install'>[] | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  if (!nodeSets?.length) return map;
  for (const nodeSet of nodeSets) {
    for (const line of getInstallLaborLines(nodeSet.install ?? {})) {
      const uid = (line.employeeUserId || '').trim();
      const rate = Number(line.ratePerDay);
      if (!uid || !(rate > 0)) continue;
      map.set(uid, rate);
    }
  }
  return map;
}

function installLaborDayRateForUser(
  user: CRMUser,
  position: PayrollPosition | undefined,
  settings: PayrollSettings,
  contractorProfile: ContractorProfile | null | undefined,
  lastUsedDayRate: number | null | undefined,
): number | null {
  if (is1099ContractorUser(user)) {
    return contractorInstallLaborDayRate(contractorProfile, lastUsedDayRate);
  }
  return rateForPosition(position, settings, user.location);
}

export type BuildInstallLaborPeopleOptions = {
  contractorProfilesByUserId?: ReadonlyMap<string, ContractorProfile | null | undefined>;
  lastInstallDayRateByUserId?: ReadonlyMap<string, number>;
};

export function buildInstallLaborPeople(
  crmUsers: CRMUser[],
  positions: PayrollPosition[],
  settings: PayrollSettings,
  options: BuildInstallLaborPeopleOptions = {},
): InstallLaborPerson[] {
  const contractorProfiles = options.contractorProfilesByUserId;
  const lastRates = options.lastInstallDayRateByUserId;
  const activePositions = positions.filter((position) => position.isActive !== false);
  const byUserId = new Map<string, PayrollPosition>();
  const byTitle = new Map<string, PayrollPosition>();
  for (const position of activePositions) {
    if (position.crmUserId && !byUserId.has(position.crmUserId)) {
      byUserId.set(position.crmUserId, position);
    }
    const title = position.title.trim().toLowerCase();
    if (title && !byTitle.has(title)) byTitle.set(title, position);
  }

  const people: InstallLaborPerson[] = [];
  const usedUserIds = new Set<string>();
  const usedNames = new Set<string>();

  for (const user of crmUsers) {
    if (!user.isActive || !isOpsHubEligibleRole(user.role)) continue;
    const name = (user.displayName || user.email || '').trim();
    if (!name) continue;
    const position = byUserId.get(user.uid) || byTitle.get(name.toLowerCase());
    people.push({
      id: user.uid,
      name,
      title: (user.title || position?.title || '').trim() || undefined,
      userId: user.uid,
      ratePerDay: installLaborDayRateForUser(
        user,
        position,
        settings,
        contractorProfiles?.get(user.uid),
        lastRates?.get(user.uid),
      ),
    });
    usedUserIds.add(user.uid);
    usedNames.add(name.toLowerCase());
  }

  for (const position of activePositions) {
    const title = position.title.trim();
    if (!title) continue;
    if (position.crmUserId && usedUserIds.has(position.crmUserId)) continue;
    if (usedNames.has(title.toLowerCase())) continue;
    const linkedUser = position.crmUserId
      ? crmUsers.find((row) => row.uid === position.crmUserId)
      : undefined;
    people.push({
      id: `payroll-${position.id}`,
      name: title,
      title,
      userId: position.crmUserId || undefined,
      ratePerDay: linkedUser
        ? installLaborDayRateForUser(
            linkedUser,
            position,
            settings,
            linkedUser.uid ? contractorProfiles?.get(linkedUser.uid) : undefined,
            linkedUser.uid ? lastRates?.get(linkedUser.uid) : undefined,
          )
        : rateForPosition(position, settings),
    });
    usedNames.add(title.toLowerCase());
  }

  return people.sort((a, b) => a.name.localeCompare(b.name));
}

/** Writes `teamLaborPerDay` from the lines so install totaling is unchanged. */
export function withLaborRollup<T extends Pick<InstallNodeData, 'teamLaborPerDay' | 'laborLines'>>(
  install: T,
  rawLines: InstallLaborLine[],
): T {
  const laborLines = rawLines.map((line) => (
    isOthersInstallLaborLine(line) ? { ...line } : { ...line, quantity: 1 }
  ));
  return {
    ...install,
    laborLines,
    teamLaborPerDay: laborLinesDayTotal(laborLines),
  };
}
